// ============================================================
// authActions.ts
// Point d'entrée unique pour la déconnexion — utilisé à la fois par :
//   • le bouton "Déconnexion" du Navbar (logout manuel)
//   • l'intercepteur 401 d'api.ts (token expiré ou invalide)
//
// Garantit qu'un token expiré déclenche EXACTEMENT le même nettoyage qu'un
// logout manuel — évite les états zombies (cache Redux d'un autre tenant,
// localStorage partiellement nettoyé, socket WS toujours connecté, etc.).
//
// Politique de vidange localStorage : DENY-LIST.
// Seules les clés liées à la session (auth, contexte tenant, données
// transactionnelles en cours) sont supprimées. Les préférences UI
// (visibilité de colonnes, filtres mémorisés…) sont préservées —
// elles sont liées au device, pas au compte.
// ============================================================

import type { Store } from '@reduxjs/toolkit';
import { logout } from '../reduxStore/AuthSlice';
import { disconnectSocket } from '../websocket/WebSocketClient';

// Le store est enregistré par Store.tsx après sa création.
// Inversion d'import nécessaire — sans ça on aurait :
//   Store → slices (qui importent api) → api → authActions → Store  (cycle)
let _store: Store | null = null;
export function registerStoreForLogout(s: Store): void { _store = s; }

export type LogoutReason = 'manual' | 'token_expired';

/** Clé sessionStorage utilisée pour signaler à LoginPage qu'on vient
 *  d'être déconnecté pour expiration de token. Lue puis supprimée
 *  par LoginPage au mount. sessionStorage survit au window.location.href. */
export const SESSION_EXPIRED_FLAG = 'auth:session_expired';

/**
 * Liste **explicite** des clés/préfixes localStorage à VIDER au logout.
 * Tout ce qui n'est pas listé ici est préservé (préférences UI).
 *
 * Pour ajouter une nouvelle clé : si elle est liée à une session/un compte
 * → l'ajouter ici. Sinon (préférence d'affichage device-local) → ne rien faire.
 */
const SESSION_LOCAL_STORAGE_KEYS: ReadonlyArray<string | RegExp> = [
  'auth_data',          // token JWT + user/owner/permissions
  'app_departement',    // département actif (contexte tenant)
  'app_all_mode',       // flag "Tous départements" (lié au contexte tenant)
  'pos_tabs_v1',        // tickets POS en cours (cart, client) — sensible
  /^assistant_chat_/,   // conversations de l'assistant IA (données métier) — une clé par user
];

/**
 * Déconnexion centralisée. Effets dans l'ordre :
 *   1. Coupe la connexion WebSocket
 *   2. Supprime UNIQUEMENT les clés session-bound du localStorage
 *      (deny-list — préférences UI mytable.*, lignedoc.* préservées)
 *   3. Vide entièrement sessionStorage puis pose SESSION_EXPIRED si pertinent
 *   4. Dispatch logout() → rootReducer reset l'état Redux complet
 *   5. Redirection hard /login → fresh-start des hooks et routes
 *
 * @param reason  'manual' (bouton) | 'token_expired' (401 intercepté)
 */
export function forceLogout(reason: LogoutReason = 'manual'): void {
  try { disconnectSocket(); } catch { /* socket pas encore init */ }

  // Suppression ciblée — ne touche pas aux préférences UI
  try {
    for (const key of SESSION_LOCAL_STORAGE_KEYS) {
      if (typeof key === 'string') {
        localStorage.removeItem(key);
      } else {
        // RegExp : itère pour matcher des préfixes/patterns futurs
        const toRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && key.test(k)) toRemove.push(k);
        }
        for (const k of toRemove) localStorage.removeItem(k);
      }
    }
  } catch { /* privacy mode */ }

  // sessionStorage : éphémère par essence → vidange totale (sauf flag posé après)
  try { sessionStorage.clear(); } catch { /* privacy mode */ }
  if (reason === 'token_expired') {
    try { sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1'); } catch { /* quota */ }
  }

  _store?.dispatch(logout());
  window.location.href = '/login';
}
